/**
 * FormField — composition wrapper: label (caption-cased uppercase tiny),
 * input slot, helper, error.
 *
 * UX §8.2 — `.label-eyebrow` style: 0.75rem uppercase tracking 0.08em.
 */
import * as React from 'react';
import { cn } from '../../lib/cn';

export interface FormFieldProps {
  /** Visible label text. */
  label: string;
  /** When the field is required, show no asterisk (we use Optional sub-label
   *  on non-required fields per UX §4.1). */
  optional?: boolean;
  /** Helper caption rendered above the input. */
  hint?: string;
  /** Error caption rendered above the input. */
  error?: string;
  /** When set, used as the htmlFor target. */
  htmlFor?: string;
  className?: string;
  children: React.ReactNode;
}

export function FormField({
  label,
  optional,
  hint,
  error,
  htmlFor,
  className,
  children,
}: FormFieldProps) {
  return (
    <label htmlFor={htmlFor} className={cn('block', className)}>
      <span className="flex items-baseline gap-2 mb-2">
        <span className="font-sans text-xs uppercase tracking-[0.08em] text-ink-500">
          {label}
        </span>
        {optional ? (
          <span className="font-sans text-[0.65rem] tracking-wide text-ink-400 italic">
            Optional
          </span>
        ) : null}
      </span>
      {hint && !error ? (
        <p className="mb-2 text-xs text-ink-500">{hint}</p>
      ) : null}
      {children}
      {error ? (
        <p role="alert" className="mt-2 text-xs italic text-error-muted">
          {error}
        </p>
      ) : null}
    </label>
  );
}
